import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Delete, Trash2, Send, Hash, AtSign, BookMarked, Type, Save, Plus, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

// English keyboard layout only for email
const englishLayout = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Z", "X", "C", "V", "B", "N", "M"],
];

const numberLayout = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["+", "-", "*", "/", "=", "(", ")", "%", "€", "$"],
];

const symbolLayout = [
  ["!", "@", "#", "$", "%", "^", "&", "*", "(", ")"],
  ["-", "_", "=", "+", "[", "]", "{", "}", "\\", "|"],
  [";", ":", "'", '"', ",", ".", "<", ">", "/", "?"],
];

// Email domain suggestions
const emailDomains = [
  "@gmail.com",
  "@yahoo.com",
  "@outlook.com",
  "@hotmail.com",
  "@icloud.com",
];

// Contact type with nickname
interface SavedContact {
  nickname: string;
  email: string;
}

// Get saved contacts from localStorage
const getSavedContacts = (): SavedContact[] => {
  try {
    const saved = localStorage.getItem("savedEmailContacts");
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

// Save contacts to localStorage
const saveContactsToStorage = (contacts: SavedContact[]) => {
  localStorage.setItem("savedEmailContacts", JSON.stringify(contacts));
};

type KeyboardMode = "letters" | "numbers" | "symbols" | "saved";

interface EmailKeyboardProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  className?: string;
}

export function EmailKeyboard({
  value,
  onChange,
  onSend,
  className,
}: EmailKeyboardProps) {
  const { lang } = useTranslation();
  const isUIHebrew = lang === "he" || lang === "ar";
  const [savedContacts, setSavedContacts] = useState<SavedContact[]>(() => getSavedContacts());
  const [mode, setMode] = useState<KeyboardMode>(() => 
    getSavedContacts().length > 0 ? "saved" : "letters"
  );
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [nicknameInput, setNicknameInput] = useState("");
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState<string | null>(null);

  // Get current layout based on mode
  const getActiveLayout = () => {
    if (mode === "letters") return englishLayout;
    if (mode === "numbers") return numberLayout;
    if (mode === "symbols") return symbolLayout;
    return englishLayout;
  };

  const activeLayout = getActiveLayout();
  const showKeyboard = mode === "letters" || mode === "numbers" || mode === "symbols";

  // Update suggestions based on current input
  useEffect(() => {
    if (!value) {
      setSuggestions([]);
      return;
    }

    // If value contains @ already, don't show domain suggestions
    if (value.includes("@")) {
      // Show domain completions if @ is at the end or incomplete domain
      const atIndex = value.lastIndexOf("@");
      const afterAt = value.slice(atIndex);
      
      if (afterAt === "@" || !afterAt.includes(".")) {
        // Filter domains that match what user typed after @
        const matchingDomains = emailDomains.filter(domain => 
          domain.startsWith(afterAt) && domain !== afterAt
        );
        setSuggestions(matchingDomains.map(d => value.slice(0, atIndex) + d));
      } else {
        setSuggestions([]);
      }
    } else {
      // Show full email suggestions with domains
      setSuggestions(emailDomains.map(domain => value + domain));
    }
  }, [value]);

  const handleKeyPress = (key: string) => {
    onChange(value + key.toLowerCase());
  };

  const handleBackspace = () => {
    onChange(value.slice(0, -1));
  };

  const handleClear = () => {
    onChange("");
  };

  const handleSpace = () => {
    // For email, space is not typically used
  };

  const handleSelectSuggestion = (suggestion: string) => {
    onChange(suggestion);
  };

  const handleSelectSavedEmail = (email: string) => {
    // Toggle selection - if already selected, deselect
    if (value === email) {
      onChange("");
    } else {
      onChange(email);
    }
    // Stay in saved mode to allow clicking Send
  };

  const handleSaveContact = () => {
    if (!value.trim() || !value.includes("@")) return;
    
    const newContact: SavedContact = {
      nickname: nicknameInput.trim() || value.split("@")[0],
      email: value,
    };
    
    // Check if email already exists
    const exists = savedContacts.some(c => c.email === value);
    if (!exists) {
      const updated = [...savedContacts, newContact];
      setSavedContacts(updated);
      saveContactsToStorage(updated);
    }
    
    setShowSaveDialog(false);
    setNicknameInput("");
  };

  const handleDeleteContact = (email: string) => {
    const updated = savedContacts.filter(c => c.email !== email);
    setSavedContacts(updated);
    saveContactsToStorage(updated);
    setDeleteConfirmEmail(null);
  };

  // Toggle mode - clicking same mode returns to letters
  const toggleMode = (newMode: KeyboardMode) => {
    if (mode === newMode) {
      setMode("letters");
    } else {
      setMode(newMode);
    }
  };

  // Toolbar items for email keyboard
  const toolbarItems = [
    { id: "numbers" as KeyboardMode, icon: Hash, label: "123" },
    { id: "symbols" as KeyboardMode, icon: AtSign, label: "#@" },
    { id: "saved" as KeyboardMode, icon: BookMarked, label: isUIHebrew ? "שמורים" : "Saved" },
  ];

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Text Input Area with Action Buttons */}
      <div className={cn(
        "flex items-stretch gap-3 mb-3",
        isUIHebrew ? "flex-row" : "flex-row-reverse"
      )}>
        {/* Right side in visual layout */}
        <div className="flex gap-2 shrink-0">
          {isUIHebrew ? (
            <>
              {/* Hebrew: Delete buttons on right (visually) */}
              <Button
                variant="outline"
                onClick={handleClear}
                disabled={!value}
                className="h-20 w-20 sm:h-24 sm:w-24 flex flex-col gap-1 p-2 [&_svg]:!size-6 sm:[&_svg]:!size-8"
              >
                <Trash2 />
                <span className="text-xs sm:text-sm font-medium">נקה</span>
              </Button>
              <Button
                variant="outline"
                onClick={handleBackspace}
                disabled={!value}
                className="h-20 w-20 sm:h-24 sm:w-24 flex flex-col gap-1 p-2 [&_svg]:!size-6 sm:[&_svg]:!size-8"
              >
                <Delete />
                <span className="text-xs sm:text-sm font-medium">מחק תו</span>
              </Button>
            </>
          ) : (
            <>
              {/* English: Save + Send buttons */}
              {showKeyboard && (
                <Button
                  onClick={() => setShowSaveDialog(true)}
                  disabled={!value.trim() || !value.includes("@")}
                  className="h-20 w-20 sm:h-24 sm:w-24 bg-primary hover:bg-primary/90 text-primary-foreground flex flex-col gap-1 p-2 [&_svg]:!size-6 sm:[&_svg]:!size-8"
                >
                  <Save />
                  <span className="text-xs sm:text-sm font-medium">Save</span>
                </Button>
              )}
              <Button
                onClick={onSend}
                disabled={!value.trim() || !value.includes("@")}
                className="h-20 w-20 sm:h-24 sm:w-24 bg-[hsl(175_40%_22%)] hover:bg-[hsl(175_40%_26%)] text-[hsl(160_60%_60%)] flex flex-col gap-1 p-2 [&_svg]:!size-6 sm:[&_svg]:!size-8"
              >
                <Send />
                <span className="text-xs sm:text-sm font-medium">Send</span>
              </Button>
            </>
          )}
        </div>

        {/* Text Display */}
        <div 
          className="flex-1 min-h-20 sm:min-h-24 max-h-28 sm:max-h-32 px-4 py-3 rounded-xl bg-card border-2 border-border flex items-center overflow-y-auto overflow-x-hidden"
          dir="ltr"
        >
          <div className={cn(
            "w-full",
            value ? "text-start" : (isUIHebrew ? "text-right" : "text-start")
          )}>
            {value ? (
              <span className="text-xl sm:text-2xl font-medium whitespace-pre-wrap break-words leading-relaxed">
                {value}
              </span>
            ) : (
              <span className="text-muted-foreground text-base sm:text-lg">
                {mode === "saved" 
                  ? (isUIHebrew ? "בחר מייל לשליחה" : "Select email to send")
                  : (isUIHebrew ? "הקלידו כתובת אימייל" : "Enter email address")
                }
              </span>
            )}
          </div>
        </div>

        {/* Left side in visual layout */}
        <div className="flex gap-2 shrink-0">
          {isUIHebrew ? (
            <>
              {/* Hebrew: Save + Send buttons on left */}
              {showKeyboard && (
                <Button
                  onClick={() => setShowSaveDialog(true)}
                  disabled={!value.trim() || !value.includes("@")}
                  className="h-20 w-20 sm:h-24 sm:w-24 bg-primary hover:bg-primary/90 text-primary-foreground flex flex-col gap-1 p-2 [&_svg]:!size-6 sm:[&_svg]:!size-8"
                >
                  <Save />
                  <span className="text-xs sm:text-sm font-medium">שמירה</span>
                </Button>
              )}
              <Button
                onClick={onSend}
                disabled={!value.trim() || !value.includes("@")}
                className="h-20 w-20 sm:h-24 sm:w-24 bg-[hsl(175_40%_22%)] hover:bg-[hsl(175_40%_26%)] text-[hsl(160_60%_60%)] flex flex-col gap-1 p-2 [&_svg]:!size-6 sm:[&_svg]:!size-8"
              >
                <Send />
                <span className="text-xs sm:text-sm font-medium">שליחה</span>
              </Button>
            </>
          ) : (
            <>
              {/* English: Delete buttons on left */}
              <Button
                variant="outline"
                onClick={handleClear}
                disabled={!value}
                className="h-20 w-20 sm:h-24 sm:w-24 flex flex-col gap-1 p-2 [&_svg]:!size-6 sm:[&_svg]:!size-8"
              >
                <Trash2 />
                <span className="text-xs sm:text-sm font-medium">Clear</span>
              </Button>
              <Button
                variant="outline"
                onClick={handleBackspace}
                disabled={!value}
                className="h-20 w-20 sm:h-24 sm:w-24 flex flex-col gap-1 p-2 [&_svg]:!size-6 sm:[&_svg]:!size-8"
              >
                <Delete />
                <span className="text-xs sm:text-sm font-medium">Backspace</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Email Domain Suggestions Row */}
      <div className="h-[48px] flex items-center justify-center gap-2 p-2 bg-muted/50 rounded-xl mb-2 overflow-hidden">
        {suggestions.slice(0, 5).map((suggestion, index) => (
          <motion.button
            key={`${suggestion}-${index}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleSelectSuggestion(suggestion)}
            className="px-4 py-2 rounded-xl font-medium transition-all text-base bg-accent text-accent-foreground hover:bg-accent/90 whitespace-nowrap"
          >
            {suggestion}
          </motion.button>
        ))}
        {suggestions.length === 0 && value && !value.includes("@") && (
          <span className="text-muted-foreground text-sm">
            {isUIHebrew ? "הקלד ובחר סיומת אימייל" : "Type and select email suffix"}
          </span>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <AnimatePresence mode="wait">
          {/* Letters/Numbers/Symbols Mode - Show Keyboard */}
          {showKeyboard && (
            <motion.div
              key="keyboard"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col gap-2 justify-center"
            >
              {/* Keyboard Grid */}
              {activeLayout.map((row, rowIndex) => (
                <div 
                  key={rowIndex} 
                  className="flex gap-2 justify-center"
                  dir="ltr"
                >
                  {row.map((key) => (
                    <motion.button
                      key={key}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleKeyPress(key)}
                      className={cn(
                        "w-[72px] h-[72px] sm:w-[84px] sm:h-[84px] md:w-[96px] md:h-[96px] rounded-xl aspect-square",
                        "bg-card hover:bg-primary/20 border-2 border-border hover:border-primary/50 transition-all",
                        "flex items-center justify-center font-bold text-foreground font-poppins",
                        mode === "letters" ? "text-2xl sm:text-3xl md:text-3xl" : "text-xl sm:text-2xl md:text-2xl"
                      )}
                    >
                      {mode === "letters" ? key.toLowerCase() : key}
                    </motion.button>
                  ))}
                </div>
              ))}

              {/* Bottom Action Row - Backspace + @ + Space + Period */}
              <div className="flex gap-2 justify-center items-center mt-2" dir="ltr">
                {/* Left side - Backspace */}
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBackspace}
                  className="w-[148px] sm:w-[172px] md:w-[196px] h-[72px] sm:h-[84px] md:h-[96px] rounded-xl bg-destructive/20 hover:bg-destructive/30 border-2 border-destructive/30 transition-all flex items-center justify-center gap-2 text-destructive font-medium"
                >
                  <Delete className="w-7 h-7 md:w-8 md:h-8" />
                </motion.button>

                {/* @ symbol - prominent */}
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onChange(value + "@")}
                  className="w-[72px] h-[72px] sm:w-[84px] sm:h-[84px] md:w-[96px] md:h-[96px] rounded-xl bg-primary/20 hover:bg-primary/30 border-2 border-primary/50 hover:border-primary transition-all flex items-center justify-center font-bold text-2xl sm:text-3xl md:text-3xl text-primary"
                >
                  @
                </motion.button>

                {/* Center - Space bar (disabled for email but keeps layout) */}
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSpace}
                  className="flex-1 max-w-[400px] h-[72px] sm:h-[84px] md:h-[96px] rounded-xl bg-muted hover:bg-muted/80 border-2 border-border transition-all flex items-center justify-center font-medium text-lg sm:text-xl md:text-xl opacity-50 cursor-not-allowed"
                  disabled
                >
                  {isUIHebrew ? "רווח" : "Space"}
                </motion.button>

                {/* Period and Hyphen for email */}
                {[".", "-"].map((punct) => (
                  <motion.button
                    key={punct}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onChange(value + punct)}
                    className="w-[72px] h-[72px] sm:w-[84px] sm:h-[84px] md:w-[96px] md:h-[96px] aspect-square rounded-xl bg-card hover:bg-primary/20 border-2 border-border hover:border-primary/50 transition-all flex items-center justify-center font-bold text-2xl sm:text-3xl md:text-3xl"
                  >
                    {punct}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Saved Emails Mode */}
          {mode === "saved" && (
            <motion.div
              key="saved"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col justify-center"
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {savedContacts.map((contact, index) => {
                  const isSelected = value === contact.email;
                  return (
                    <motion.button
                      key={contact.email}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.03 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSelectSavedEmail(contact.email)}
                      className={cn(
                        "p-4 rounded-2xl border-2 transition-all flex flex-col items-center justify-center gap-1 min-h-[100px] relative group",
                        isSelected 
                          ? "bg-primary border-primary text-primary-foreground" 
                          : "bg-card border-border hover:border-primary hover:bg-primary/10"
                      )}
                    >
                      {/* Delete button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmEmail(contact.email);
                        }}
                        className={cn(
                          "absolute top-2 right-2 rtl:right-auto rtl:left-2 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity",
                          isSelected ? "bg-primary-foreground/20 hover:bg-primary-foreground/30" : "bg-destructive/10 hover:bg-destructive/20"
                        )}
                      >
                        <Trash2 className={cn("w-4 h-4", isSelected ? "text-primary-foreground" : "text-destructive")} />
                      </button>
                      <User className={cn(
                        "w-8 h-8 mb-1",
                        isSelected ? "text-primary-foreground" : "text-muted-foreground"
                      )} />
                      <span className={cn(
                        "text-lg font-bold",
                        isSelected ? "text-primary-foreground" : "text-foreground"
                      )}>
                        {contact.nickname}
                      </span>
                      <span className={cn(
                        "text-sm",
                        isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
                      )}>
                        {contact.email}
                      </span>
                    </motion.button>
                  );
                })}

                {/* Add new contact button - always at the end */}
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: savedContacts.length * 0.03 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setMode("letters")}
                  className="p-4 rounded-2xl bg-primary/10 border-2 border-dashed border-primary/50 hover:border-primary hover:bg-primary/20 transition-all flex flex-col items-center justify-center gap-2 min-h-[100px]"
                >
                  <Plus className="w-8 h-8 text-primary" />
                  <span className="text-sm font-medium text-primary">
                    {isUIHebrew ? "הוסף חדש" : "Add new"}
                  </span>
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Fixed Bottom Toolbar */}
      <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t border-border">
        {/* Letters button to return to default */}
        <Button
          variant={mode === "letters" ? "default" : "outline"}
          onClick={() => setMode("letters")}
          className={cn(
            "h-12 px-4 gap-2",
            mode === "letters" && "bg-primary text-primary-foreground"
          )}
        >
          <Type className="w-4 h-4" />
          {isUIHebrew ? "אותיות" : "ABC"}
        </Button>

        {/* Toolbar items */}
        {toolbarItems.map((item) => {
          const isActive = mode === item.id;
          
          return (
            <Button
              key={item.id}
              variant={isActive ? "default" : "outline"}
              onClick={() => toggleMode(item.id)}
              className={cn(
                "h-12 px-4 gap-2",
                isActive && "bg-primary text-primary-foreground"
              )}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Button>
          );
        })}
      </div>

      {/* Save Contact Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-md" dir={isUIHebrew ? "rtl" : "ltr"}>
          <DialogHeader>
            <DialogTitle>
              {isUIHebrew ? "שמור איש קשר" : "Save Contact"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {isUIHebrew ? "כינוי" : "Nickname"}
              </label>
              <Input
                value={nicknameInput}
                onChange={(e) => setNicknameInput(e.target.value)}
                placeholder={isUIHebrew ? "לדוגמה: בנג'י המטפל" : "e.g. John the caregiver"}
                className="text-lg"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {isUIHebrew ? "אימייל" : "Email"}
              </label>
              <div className="p-3 bg-muted rounded-lg text-base" dir="ltr">
                {value}
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                {isUIHebrew ? "ביטול" : "Cancel"}
              </Button>
              <Button onClick={handleSaveContact}>
                {isUIHebrew ? "שמור" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmEmail} onOpenChange={(open) => !open && setDeleteConfirmEmail(null)}>
        <DialogContent className="sm:max-w-md" dir={isUIHebrew ? "rtl" : "ltr"}>
          <DialogHeader>
            <DialogTitle>
              {isUIHebrew ? "מחיקת איש קשר" : "Delete Contact"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-base text-muted-foreground">
              {isUIHebrew 
                ? `האם אתה בטוח שברצונך למחוק את "${savedContacts.find(c => c.email === deleteConfirmEmail)?.nickname || deleteConfirmEmail}"?`
                : `Are you sure you want to delete "${savedContacts.find(c => c.email === deleteConfirmEmail)?.nickname || deleteConfirmEmail}"?`
              }
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setDeleteConfirmEmail(null)}>
                {isUIHebrew ? "ביטול" : "Cancel"}
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => deleteConfirmEmail && handleDeleteContact(deleteConfirmEmail)}
              >
                <Trash2 className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
                {isUIHebrew ? "מחק" : "Delete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
